import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Label } from './ui/label';
import { 
  Plus, 
  Search, 
  Filter,
  UserCheck,
  Phone,
  Mail,
  Building,
  Edit,
  Trash2,
  Eye,
  MessageSquare
} from 'lucide-react';
import EmailHistory from './EmailHistory';
import ContactProfile from './ContactProfile';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const Contacts = ({ selectedLocation = 'All Locations' }) => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddContact, setShowAddContact] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  // viewingContact state removed - now using dedicated profile pages
  // Gmail connection moved to Settings page
  const [showEmailHistory, setShowEmailHistory] = useState(null);
  const [showCreateCompany, setShowCreateCompany] = useState(false);
  const [newCompany, setNewCompany] = useState({
    name: '',
    industry: '',
    website: '',
    notes: ''
  });
  
  const { user } = useAuth();

  // Contact form state
  const [newContact, setNewContact] = useState({
    name: '',
    email: '',
    phone: '',
    title: '',
    company_id: 'no-company',
    status: 'Prospect',
    notes: '',
    lead_source: ''
  });

  useEffect(() => {
    loadData();
  }, [selectedLocation, statusFilter]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = {};
      if (statusFilter !== 'all') {
        filters.status = statusFilter;
      }

      const [contactsData, companiesData] = await Promise.all([
        apiService.getCRMContacts(filters),
        apiService.getCRMCompanies()
      ]);

      setContacts(contactsData);
      setCompanies(companiesData);
    } catch (error) {
      console.error('Error loading contacts data:', error);
      setError('Failed to load contacts data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddContact = async () => {
    if (!newContact.name || !newContact.email) {
      setError('Name and email are required');
      return;
    }

    try {
      // Prepare contact data, converting "no-company" to empty string for backend
      const contactData = {
        ...newContact,
        company_id: newContact.company_id === 'no-company' ? '' : newContact.company_id
      };
      
      await apiService.createCRMContact(contactData);
      
      setNewContact({
        name: '',
        email: '',
        phone: '',
        title: '',
        company_id: 'no-company',
        status: 'Prospect',
        notes: '',
        lead_source: ''
      });
      setShowAddContact(false);
      await loadData();
    } catch (error) {
      console.error('Error adding contact:', error);
      setError('Failed to add contact');
    }
  };

  const handleUpdateContact = async () => {
    if (!editingContact) return;

    try {
      await apiService.updateCRMContact(editingContact.id, editingContact);
      setEditingContact(null);
      await loadData();
    } catch (error) {
      console.error('Error updating contact:', error);
      setError('Failed to update contact');
    }
  };

  const handleCreateCompany = async () => {
    if (!newCompany.name) {
      setError('Company name is required');
      return;
    }

    try {
      const companyData = {
        name: newCompany.name,
        industry: newCompany.industry || '',
        website: newCompany.website || '',
        notes: newCompany.notes || '',
        status: 'Prospect'
      };

      const createdCompany = await apiService.createCRMCompany(companyData);
      
      // Reset the new company form
      setNewCompany({
        name: '',
        industry: '',
        website: '',
        notes: ''
      });
      
      // Close the create company dialog
      setShowCreateCompany(false);
      
      // Update the contact's company_id to the newly created company
      setNewContact(prev => ({ ...prev, company_id: createdCompany.id }));
      
      // Reload data to refresh the companies list
      await loadData();
    } catch (error) {
      console.error('Error creating company:', error);
      setError('Failed to create company');
    }
  };

  const handleStatusChange = async (contactId, newStatus) => {
    try {
      await apiService.updateCRMContactStatus(contactId, newStatus);
      await loadData();
    } catch (error) {
      console.error('Error updating status:', error);
      setError('Failed to update status');
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) {
      return;
    }

    try {
      await apiService.deleteCRMContact(contactId);
      await loadData();
    } catch (error) {
      console.error('Error deleting contact:', error);
      setError('Failed to delete contact');
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'Prospect':
        return 'bg-yellow-100 text-yellow-800';
      case 'Member':
        return 'bg-green-100 text-green-800';
      case 'Former Member':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : 'No Company';
  };

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (contact.title && contact.title.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesSearch;
  });

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading contacts...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <UserCheck className="w-8 h-8 mr-3 text-blue-600" />
            Contacts
          </h1>
          <p className="text-gray-600 mt-2">Manage your CRM contacts and prospects</p>
        </div>
        
        <Dialog open={showAddContact} onOpenChange={setShowAddContact}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Contact
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Add New Contact</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    placeholder="Contact name"
                    value={newContact.name}
                    onChange={(e) => setNewContact(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="contact@company.com"
                    value={newContact.email}
                    onChange={(e) => setNewContact(prev => ({ ...prev, email: e.target.value }))}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    placeholder="+1 (555) 123-4567"
                    value={newContact.phone}
                    onChange={(e) => setNewContact(prev => ({ ...prev, phone: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    placeholder="Job title"
                    value={newContact.title}
                    onChange={(e) => setNewContact(prev => ({ ...prev, title: e.target.value }))}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="company">Company</Label>
                  <Select
                    value={newContact.company_id}
                    onValueChange={(value) => {
                      if (value === 'create-new-company') {
                        setShowCreateCompany(true);
                        return;
                      }
                      setNewContact(prev => ({ ...prev, company_id: value }));
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select company (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no-company">No Company</SelectItem>
                      <SelectItem value="create-new-company">+ Create New Company</SelectItem>
                      {companies.map((company) => (
                        <SelectItem key={company.id} value={company.id}>
                          {company.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={newContact.status}
                    onValueChange={(value) => setNewContact(prev => ({ ...prev, status: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Prospect">Prospect</SelectItem>
                      <SelectItem value="Member">Member</SelectItem>
                      <SelectItem value="Former Member">Former Member</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="lead_source">Lead Source</Label>
                <Input
                  id="lead_source"
                  placeholder="e.g. Website, Referral, Cold Call"
                  value={newContact.lead_source}
                  onChange={(e) => setNewContact(prev => ({ ...prev, lead_source: e.target.value }))}
                />
              </div>

              <div>
                <Label htmlFor="notes">Notes</Label>
                <textarea
                  id="notes"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                  placeholder="Additional notes about the contact"
                  value={newContact.notes}
                  onChange={(e) => setNewContact(prev => ({ ...prev, notes: e.target.value }))}
                />
              </div>

              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setShowAddContact(false)}>
                  Cancel
                </Button>
                <Button onClick={handleAddContact}>
                  Create Contact
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="flex space-x-4">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
          <Input
            placeholder="Search contacts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="Prospect">Prospects</SelectItem>
            <SelectItem value="Member">Members</SelectItem>
            <SelectItem value="Former Member">Former Members</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Contacts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredContacts.map((contact) => (
          <Card 
            key={contact.id} 
            className="hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => navigate(`/crm/contacts/${contact.id}`)}
          >
            <CardHeader className="pb-3">
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-lg">{contact.name}</CardTitle>
                  <p className="text-sm text-gray-600">{contact.title || 'No Title'}</p>
                </div>
                <Badge className={getStatusBadgeColor(contact.status)}>
                  {contact.status}
                </Badge>
              </div>
            </CardHeader>
            
            <CardContent className="space-y-3">
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <Mail className="w-4 h-4" />
                <span className="truncate">{contact.email}</span>
              </div>
              
              {contact.phone && (
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <Phone className="w-4 h-4" />
                  <span>{contact.phone}</span>
                </div>
              )}
              
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <Building className="w-4 h-4" />
                <span className="truncate">{getCompanyName(contact.company_id)}</span>
              </div>

              {contact.lead_source && (
                <div className="text-xs text-gray-500">
                  Source: {contact.lead_source}
                </div>
              )}

              <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                <div onClick={(e) => e.stopPropagation()}>
                  <Select
                    value={contact.status}
                    onValueChange={(value) => handleStatusChange(contact.id, value)}
                  >
                    <SelectTrigger className="w-32 h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Prospect">Prospect</SelectItem>
                      <SelectItem value="Member">Member</SelectItem>
                      <SelectItem value="Former Member">Former Member</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex space-x-1" onClick={(e) => e.stopPropagation()}>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowEmailHistory(contact.id);
                    }}
                    title="View email history"
                  >
                    <MessageSquare className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingContact(contact);
                    }}
                    title="Edit contact"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteContact(contact.id);
                    }}
                    className="text-red-600 hover:text-red-700"
                    title="Delete contact"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredContacts.length === 0 && (
        <div className="text-center py-12">
          <UserCheck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No contacts found</h3>
          <p className="text-gray-500 mb-6">
            {searchTerm || statusFilter !== 'all' 
              ? 'No contacts match your current filters' 
              : 'Get started by adding your first contact'
            }
          </p>
          <Button onClick={() => setShowAddContact(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Contact
          </Button>
        </div>
      )}

      {/* Contact Profile Dialog - Removed: Now using dedicated profile pages */}

      {/* Email History Dialog */}
      {showEmailHistory && (
        <Dialog open={!!showEmailHistory} onOpenChange={() => setShowEmailHistory(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle>
                Email History - {contacts.find(c => c.id === showEmailHistory)?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="h-[70vh] overflow-y-auto">
              <EmailHistory 
                contactId={showEmailHistory} 
                title={`Email History for ${contacts.find(c => c.id === showEmailHistory)?.name}`}
              />
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Create Company Dialog */}
      <Dialog open={showCreateCompany} onOpenChange={setShowCreateCompany}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Company</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="companyName">Company Name *</Label>
              <Input
                id="companyName"
                placeholder="Enter company name"
                value={newCompany.name}
                onChange={(e) => setNewCompany(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            
            <div>
              <Label htmlFor="companyIndustry">Industry</Label>
              <Input
                id="companyIndustry"
                placeholder="e.g., Technology, Finance, Healthcare"
                value={newCompany.industry}
                onChange={(e) => setNewCompany(prev => ({ ...prev, industry: e.target.value }))}
              />
            </div>
            
            <div>
              <Label htmlFor="companyWebsite">Website</Label>
              <Input
                id="companyWebsite"
                placeholder="https://company.com"
                value={newCompany.website}
                onChange={(e) => setNewCompany(prev => ({ ...prev, website: e.target.value }))}
              />
            </div>
            
            <div>
              <Label htmlFor="companyNotes">Notes</Label>
              <Input
                id="companyNotes"
                placeholder="Additional information"
                value={newCompany.notes}
                onChange={(e) => setNewCompany(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowCreateCompany(false);
                  setNewCompany({ name: '', industry: '', website: '', notes: '' });
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleCreateCompany}>
                Create Company
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Contacts;